// src/pages/misc/public/learnContent.ts
// [HANDOFF] All the text for /como-funciona: TOPICS (the filter chips), VIDEOS (cards, there is no real video file yet, the popup says
//   "Video en camino") and ARTICLES (6 short articles, each with a slug used in the URL). Edit here to change content; no code changes needed.
//   Article body is an array of paragraphs (plain strings, no HTML). [I18N] Typed Spanish; the topic names are also the filter labels, so
//   a translation must keep `topic` values and the TOPICS list identical.
export type Topic = 'Qué es' | 'El día a día' | 'La plataforma' | 'Equipo';

export interface LearnVideo {
  id: string;
  topic: Topic;
  title: string;
  hook: string;
  note: string;
  duration: string;
}

export interface LearnArticle {
  slug: string;
  topic: Topic;
  title: string;
  excerpt: string;
  minutes: number;
  body: string[];
}

export const TOPICS: Topic[] = ['Qué es', 'El día a día', 'La plataforma', 'Equipo'];

export const VIDEOS: LearnVideo[] = [
  {
    id: 'crm',
    topic: 'Qué es',
    title: 'Qué es un CRM, en un minuto',
    hook: 'Una libreta que casi nadie llena.',
    note: 'Quién es el lead, qué busca, en qué etapa va y cuándo le toca el siguiente mensaje. El problema: alguien tiene que escribirlo, casi siempre a mano, al final del día.',
    duration: '1:10',
  },
  {
    id: 'dia',
    topic: 'El día a día',
    title: 'El día a día con un CRM normal',
    hook: 'Celular, WhatsApp, CRM, otra vez.',
    note: 'Sacas el celular, contestas, abres el CRM y capturas todo a mano. Con Ventaje ese brinco desaparece: documentar deja de comerse el día.',
    duration: '1:40',
  },
  {
    id: 'tres',
    topic: 'La plataforma',
    title: 'Las 3 páginas de Ventaje',
    hook: 'Dashboard, leads y donde platicas.',
    note: 'Dashboard: cómo vas. Leads: a quién le das seguimiento, cada uno con su ficha. Workspace: tu WhatsApp, adentro.',
    duration: '2:20',
  },
  {
    id: 'ficha',
    topic: 'La plataforma',
    title: 'La ficha se llena sola',
    hook: 'Tú vendes. Ella se escribe.',
    note: 'Qué busca, presupuesto, etapa y el siguiente seguimiento. Ligada a su proyecto, para que la IA sepa de qué habla.',
    duration: '1:30',
  },
  {
    id: 'equipo',
    topic: 'Equipo',
    title: 'Todo el equipo, a la vista',
    hook: 'Si un asesor se va, la info se queda.',
    note: 'Cada asesor, cada lead y cada seguimiento en un solo lugar. Conversaciones, fichas y llamadas son de la empresa.',
    duration: '1:50',
  },
];

export const ARTICLES: LearnArticle[] = [
  {
    slug: 'que-es-un-crm',
    topic: 'Qué es',
    title: 'Qué es un CRM y por qué nadie lo llena',
    excerpt: 'Es una libreta inteligente de tus clientes. El problema no es la libreta: es que alguien tiene que escribirla a mano.',
    minutes: 3,
    body: [
      'Un CRM guarda quién es cada lead, qué busca, en qué etapa va y cuándo le toca el siguiente mensaje.',
      'En un CRM normal esa libreta la llena una persona, casi siempre al final del día. Casi nunca pasa. El lead se queda en WhatsApp, entre el chat de la familia y el del notario.',
      'Ventaje ya viene hecho para bienes raíces. Proyectos, leads y seguimiento no hay que inventarlos. La configuración inicial son unos 5 minutos, según el tamaño de la cuenta, y te ayudamos si lo necesitas.',
    ],
  },
  {
    slug: 'hecho-para-bienes-raices',
    topic: 'Qué es',
    title: 'Hecho para bienes raíces, no para cualquier negocio',
    excerpt: 'Un CRM genérico sirve para todo, por eso tienes que configurarlo tú. Ventaje ya trae proyectos, etapas y seguimiento.',
    minutes: 3,
    body: [
      'Un CRM genérico no sabe qué es un desarrollo, una visita o un cierre. Por eso la primera semana se va en configurarlo.',
      'Ventaje llega con tres cosas listas: proyectos y desarrollos, leads ligados a cada proyecto, y etapas con su seguimiento.',
      'Se hace una vez. Después el día es contestar, no capturar.',
    ],
  },
  {
    slug: 'dashboard-leads-workspace',
    topic: 'La plataforma',
    title: 'Dashboard, leads y workspace',
    excerpt: 'Tres páginas. Cómo vas, a quién le das seguimiento, y donde platicas.',
    minutes: 4,
    body: [
      'El dashboard muestra tus métricas de un vistazo. No armas reportes: se calculan con lo que Ventaje ya documentó.',
      'En leads, cada persona está en su etapa, con la fecha del siguiente seguimiento y la fuente de donde llegó.',
      'El workspace es tu WhatsApp adentro de Ventaje. Contestar y documentar pasan en el mismo lugar. Ves qué escribiste tú y qué escribió la IA. Las llamadas quedan grabadas.',
    ],
  },
  {
    slug: 'la-ficha-se-llena-sola',
    topic: 'El día a día',
    title: 'La ficha se llena sola mientras vendes',
    excerpt: 'Qué busca, presupuesto, etapa y el siguiente mensaje. Tú solo la revisas.',
    minutes: 3,
    body: [
      'Hoy el ciclo es: sacas el celular, contestas en WhatsApp, abres el CRM y capturas todo a mano. Y otra vez, con cada lead.',
      'Con Ventaje la ficha se escribe mientras platicas. Queda ligada a su proyecto, así la IA sabe de qué está hablando.',
      'En lo mínimo, tú contestas y Ventaje documenta. En lo máximo, la IA escribe y hasta llama, con tu voz, solo si tú quieres.',
    ],
  },
  {
    slug: 'si-un-asesor-se-va',
    topic: 'Equipo',
    title: 'Si un asesor se va, la información se queda',
    excerpt: 'Conversaciones, fichas y llamadas son de la empresa, no del celular de alguien.',
    minutes: 3,
    body: [
      'El dueño ve cada asesor, cada lead y cada seguimiento en un solo lugar.',
      'Cuando alguien se va, no se lleva el contexto. Las conversaciones, las fichas y las llamadas grabadas se quedan en la cuenta.',
      'Eso también sirve si trabajas solo: no dependes de tu memoria ni de un chat enterrado.',
    ],
  },
  {
    slug: 'ventaje-aprende-contigo',
    topic: 'La plataforma',
    title: 'Lo que viene: Ventaje aprende contigo',
    excerpt: 'De tus propias conversaciones aprende qué vendes y cómo hablas. Mientras más lo usas, mejor asistente se vuelve.',
    minutes: 2,
    body: [
      'Hoy Ventaje ya documenta y puede escribir o llamar con tu voz.',
      'Lo que sigue es que aprenda de tus conversaciones reales: qué vendes y cómo se lo dices a un lead.',
      'Mientras más lo usas, menos suena a un guion y más suena a ti.',
    ],
  },
];

export function findArticle(slug: string): LearnArticle | undefined {
  return ARTICLES.find((a) => a.slug === slug);
}
